import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { modalsService } from '~/services/Modals';

import { mainStore, orderStore, userStore } from '~/stores';

let reopenModal: () => ModalType | null = () => null;

export const MODAL_EDIT_ADDRESS_PARAM = 'editAddress';

export const useModal = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [modalType, setModalType] = useState<string | null>(
    searchParams.get('modal'),
  );

  useEffect(() => {
    setModalType(searchParams.get('modal'));
  }, [searchParams]);

  useEffect(() => {
    if (modalType) {
      document.body.classList.add('body-no-scroll');
    } else {
      document.body.classList.remove('body-no-scroll');
      modalsService.clearHistory();
    }

    return () => {
      if (!modalType) {
        document.body.classList.remove('body-no-scroll');
      }
    };
  }, [modalType]);

  const openModal = (
    name: ModalType,
    source?: string,
    additionalParams?: Record<string, string>,
    redirectToMap?: boolean,
    withReplace?: boolean,
  ) => {
    if (
      name === ModalType.Auth &&
      redirectToMap &&
      (!userStore.deliveryAddress || !orderStore.etaCalculation)
    ) {
      name = company.showNewAddressModal
        ? ModalType.EditAddressLocation
        : ModalType.DeliveryAddress;
      reopenModal = () =>
        orderStore.etaCalculation &&
        userStore.deliveryAddress &&
        ModalType.Auth;
    }
    if (name === ModalType.DeliveryInfo) {
      mainStore.sendToRN('logAmplitudeEvent', {
        name: 'Delivery terms open',
        params: { source },
      });
    }
    if (modalType) {
      modalsService.pushToHistory(modalType as ModalType, searchParams);
    }
    const newParams = new URLSearchParams();

    newParams.set('modal', name);

    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, val]) => {
        if (val) {
          newParams.set(key, val);
        }
      });
    }

    setSearchParams(newParams, { replace: withReplace });
    return newParams;
  };

  const closeModal = (paramsToRemove?: string[]) => {
    const reopenModalName = reopenModal();
    if (reopenModalName) {
      searchParams.set('modal', reopenModalName);
      reopenModal = () => null;
      setSearchParams(searchParams, { replace: true });
    } else {
      searchParams.delete('modal');
      searchParams.delete(MODAL_EDIT_ADDRESS_PARAM);
      searchParams.delete('source');
      searchParams.delete('pickup');
      if (paramsToRemove?.length) {
        for (const param of paramsToRemove) {
          if (searchParams.has(param)) {
            searchParams.delete(param);
          }
        }
      }
      modalsService.clearHistory();
      setSearchParams(searchParams, { replace: true });
    }
    return searchParams;
  };

  const goBack = (paramsToRemove?: string[]) => {
    const previousModal = modalsService.popFromHistory();

    if (previousModal) {
      const prevParams = new URLSearchParams(previousModal.params);
      setSearchParams(prevParams);
      return prevParams;
    }

    closeModal(paramsToRemove);
  };

  const popToTop = () => {
    while (modalsService.hasHistory) {
      goBack();
    }
  };

  return {
    openModal,
    closeModal,
    goBack,
    popToTop,
    hasHistory: modalsService.hasHistory,
  };
};
